import { FC, useCallback, useEffect, useRef, useState } from 'react'

import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import {
  Autocomplete,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'

const DEFAULT_FONT_SIZE_PX = 14
const DEFAULT_FONT_SIZE = `${DEFAULT_FONT_SIZE_PX}px`

const formatFontSize = (raw: string | number): string => {
  const num = Number(String(raw).replace(/\D/g, ''))
  if (!Number.isFinite(num) || num <= 0) return DEFAULT_FONT_SIZE
  return `${num}px`
}

const parseFontSize = (raw: string): number | null => {
  const num = Number(String(raw).replace(/\D/g, ''))
  if (!Number.isFinite(num) || num < 8 || num > 72) return null
  return num
}

const FONT_TAG_SIZE_MAP: Record<string, number> = {
  '1': 10,
  '2': 13,
  '3': 16,
  '4': 18,
  '5': 24,
  '6': 32,
  '7': 48,
}

const TEXT_COLORS = [
  '#000000',
  '#E53E3E',
  '#DD6B20',
  '#D69E2E',
  '#38A169',
  '#3182CE',
  '#805AD5',
  '#718096',
] as const

type ToolbarState = {
  bold: boolean
  italic: boolean
  underline: boolean
  unorderedList: boolean
  orderedList: boolean
  fontSize: string
  color: string
}

type Props = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  editorKey?: string
  showToolbar?: boolean
}

const DEFAULT_TOOLBAR_STATE: ToolbarState = {
  bold: false,
  italic: false,
  underline: false,
  unorderedList: false,
  orderedList: false,
  fontSize: DEFAULT_FONT_SIZE,
  color: '#000000',
}

const isHtmlContent = (value: string) => /<[a-z][\s\S]*>/i.test(value)

const toEditorHtml = (value: string) =>
  isHtmlContent(value) ? value : value.replace(/\n/g, '<br>')

const rgbToHex = (rgb: string): string | null => {
  if (!rgb) return null
  const trimmed = rgb.trim().toLowerCase()
  if (trimmed.startsWith('#')) {
    if (trimmed.length === 4) {
      return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`
    }
    return trimmed.length === 7 ? trimmed : null
  }
  const match = trimmed.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/)
  if (!match) return null
  return `#${[match[1], match[2], match[3]]
    .map((n) => Number(n).toString(16).padStart(2, '0'))
    .join('')}`
}

const hexToRgb = (hex: string) => {
  const normalized = rgbToHex(hex)
  if (!normalized) return null
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  }
}

const nearestPaletteColor = (hex: string): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return '#000000'

  let best = TEXT_COLORS[0]
  let bestDist = Infinity
  for (const palette of TEXT_COLORS) {
    const p = hexToRgb(palette)
    if (!p) continue
    const dist =
      (rgb.r - p.r) ** 2 + (rgb.g - p.g) ** 2 + (rgb.b - p.b) ** 2
    if (dist < bestDist) {
      bestDist = dist
      best = palette
    }
  }
  return best
}

const hexToRgbString = (hex: string): string => {
  const rgb = hexToRgb(hex)
  return rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : hex
}

const FONT_SIZE_PRESETS = ['10', '12', '14', '16', '18', '20', '24', '28', '32']

const parseInlineFontSizePx = (fontSize: string): number | null => {
  if (!fontSize) return null
  const trimmed = fontSize.trim().toLowerCase()

  if (trimmed.endsWith('px')) {
    const px = Math.round(parseFloat(trimmed))
    return Number.isFinite(px) && px >= 8 && px <= 72 ? px : null
  }

  if (trimmed.endsWith('pt')) {
    const pt = parseFloat(trimmed)
    if (!Number.isFinite(pt)) return null
    const px = Math.round(pt * (96 / 72))
    return px >= 8 && px <= 72 ? px : null
  }

  return null
}

const getComputedFontSizePx = (el: HTMLElement): number => {
  const px = Math.round(parseFloat(window.getComputedStyle(el).fontSize))
  return Number.isFinite(px) && px >= 8 && px <= 72 ? px : DEFAULT_FONT_SIZE_PX
}

const getExplicitFontSizePx = (el: HTMLElement): number | null => {
  if (el.style.fontSize) {
    const px = parseInlineFontSizePx(el.style.fontSize)
    if (px) return px
  }

  if (el.tagName === 'FONT') {
    const sizeAttr = el.getAttribute('size')
    if (sizeAttr && FONT_TAG_SIZE_MAP[sizeAttr]) {
      return FONT_TAG_SIZE_MAP[sizeAttr]
    }
  }

  return null
}

const getExplicitColor = (el: HTMLElement): string | null => {
  if (el.style.color) {
    const hex = rgbToHex(el.style.color)
    if (hex) return nearestPaletteColor(hex)
  }

  if (el.tagName === 'FONT') {
    const fontColor = el.getAttribute('color')
    if (fontColor) {
      const hex = rgbToHex(fontColor)
      if (hex) return nearestPaletteColor(hex)
    }
  }

  return null
}

const getFontSizeForNode = (
  node: Node | null,
  editor: HTMLElement
): number | null => {
  let el = getElementFromNode(node)
  while (el && editor.contains(el)) {
    const explicit = getExplicitFontSizePx(el)
    if (explicit) return explicit
    el = el.parentElement
  }

  const anchor = getElementFromNode(node)
  return anchor && editor.contains(anchor) ? getComputedFontSizePx(anchor) : null
}

const getColorForNode = (
  node: Node | null,
  editor: HTMLElement
): string | null => {
  let el = getElementFromNode(node)
  while (el && editor.contains(el)) {
    const explicit = getExplicitColor(el)
    if (explicit) return explicit
    el = el.parentElement
  }
  return null
}

const getSelectionFontSize = (editor: HTMLElement): string => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return DEFAULT_FONT_SIZE

  const range = selection.getRangeAt(0)
  if (range.collapsed) return DEFAULT_FONT_SIZE

  const sizes = new Set<number>()
  const walker = document.createTreeWalker(
    editor,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) =>
        range.intersectsNode(node)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT,
    }
  )

  let textNode = walker.nextNode()
  while (textNode) {
    const size = getFontSizeForNode(textNode, editor)
    if (size) sizes.add(size)
    textNode = walker.nextNode()
  }

  if (sizes.size === 0) {
    const anchorSize = getFontSizeForNode(selection.anchorNode, editor)
    return anchorSize ? formatFontSize(anchorSize) : DEFAULT_FONT_SIZE
  }

  return formatFontSize([...sizes][0])
}

const getSelectionColor = (editor: HTMLElement): string => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return '#000000'

  const range = selection.getRangeAt(0)
  if (range.collapsed) return '#000000'

  const colors = new Set<string>()
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      range.intersectsNode(node)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  })

  let textNode = walker.nextNode()
  while (textNode) {
    const color = getColorForNode(textNode, editor)
    if (color) colors.add(color)
    textNode = walker.nextNode()
  }

  if (colors.size === 0) {
    const anchorColor = getColorForNode(selection.anchorNode, editor)
    return anchorColor ?? '#000000'
  }

  return [...colors][0]
}

const getElementFromNode = (node: Node | null): HTMLElement | null => {
  if (!node) return null
  return node.nodeType === Node.TEXT_NODE
    ? node.parentElement
    : (node as HTMLElement)
}

const fontSizeFieldSx = {
  width: 52,
  '& .MuiOutlinedInput-root': {
    height: 28,
    minHeight: 28,
    py: 0,
    fontSize: '0.8125rem',
  },
  '& .MuiOutlinedInput-input': {
    p: '2px 4px !important',
    textAlign: 'center',
  },
  '& .MuiAutocomplete-endAdornment': {
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    '& .MuiButtonBase-root': { p: 0.25 },
    '& .MuiSvgIcon-root': { fontSize: 16 },
  },
} as const

const isTouchPointer = (e: { pointerType?: string }) =>
  e.pointerType === 'touch' || e.pointerType === 'pen'

const RichTextEditor: FC<Props> = ({
  value,
  onChange,
  placeholder = 'Skriv ditt meddelande...',
  minHeight = 120,
  editorKey,
  showToolbar = true,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true })
  const editorRef = useRef<HTMLDivElement>(null)
  const savedSelectionRef = useRef<Range | null>(null)
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null)
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState<HTMLElement | null>(
    null
  )
  const [toolbar, setToolbar] = useState<ToolbarState>(DEFAULT_TOOLBAR_STATE)
  const [fontSizeInput, setFontSizeInput] = useState(DEFAULT_FONT_SIZE)
  const [fontSizeOpen, setFontSizeOpen] = useState(false)
  const fontSizeEditingRef = useRef(false)
  const fontSizeIgnoreCloseRef = useRef(false)

  const emitChange = useCallback(() => {
    const el = editorRef.current
    if (!el) return
    const html = el.innerHTML
    const text = el.textContent?.trim() || ''
    onChange(text ? html : '')
  }, [onChange])

  const saveSelection = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    const editor = editorRef.current
    if (!editor?.contains(range.commonAncestorContainer)) return

    if (
      range.collapsed &&
      document.activeElement !== editor &&
      savedSelectionRef.current &&
      !savedSelectionRef.current.collapsed
    ) {
      return
    }

    savedSelectionRef.current = range.cloneRange()
  }, [])

  const restoreSelection = useCallback(() => {
    const range = savedSelectionRef.current
    if (!range) return false
    const selection = window.getSelection()
    if (!selection) return false
    try {
      selection.removeAllRanges()
      selection.addRange(range)
      return true
    } catch {
      return false
    }
  }, [])

  const focusEditor = useCallback(() => {
    editorRef.current?.focus({ preventScroll: true })
    restoreSelection()
  }, [restoreSelection])

  const readToolbarState = useCallback((): ToolbarState => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return DEFAULT_TOOLBAR_STATE

    const anchor = selection.anchorNode
    if (!anchor || !editorRef.current?.contains(anchor)) {
      return DEFAULT_TOOLBAR_STATE
    }

    const editor = editorRef.current
    const fontSize = getSelectionFontSize(editor)
    const color = getSelectionColor(editor)

    return {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      orderedList: document.queryCommandState('insertOrderedList'),
      fontSize,
      color,
    }
  }, [])

  const refreshToolbarState = useCallback(() => {
    const next = readToolbarState()
    setToolbar(next)
    if (!fontSizeEditingRef.current) {
      setFontSizeInput(next.fontSize)
    }
  }, [readToolbarState])

  const keepSelection = useCallback(() => {
    focusEditor()
    saveSelection()
  }, [focusEditor, saveSelection])

  const openFontSizeDropdown = useCallback(() => {
    saveSelection()
    fontSizeEditingRef.current = true
    fontSizeIgnoreCloseRef.current = true
    setFontSizeOpen(true)
    requestAnimationFrame(() => {
      fontSizeIgnoreCloseRef.current = false
    })
  }, [saveSelection])

  const closeFontSizeDropdown = useCallback(() => {
    if (fontSizeIgnoreCloseRef.current) return
    fontSizeEditingRef.current = false
    setFontSizeOpen(false)
    setFontSizeInput(toolbar.fontSize)
    keepSelection()
  }, [keepSelection, toolbar.fontSize])

  const applyInlineStyle = useCallback(
    (styles: { fontSize?: string; color?: string }) => {
      focusEditor()
      restoreSelection()
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return

      const range = selection.getRangeAt(0)
      if (range.collapsed) return

      if (styles.color) {
        document.execCommand('styleWithCSS', false, 'true')
        document.execCommand('foreColor', false, hexToRgbString(styles.color))
      }

      if (styles.fontSize) {
        const span = document.createElement('span')
        span.style.fontSize = styles.fontSize
        const fragment = range.extractContents()
        span.appendChild(fragment)
        range.insertNode(span)

        const newRange = document.createRange()
        newRange.selectNodeContents(span)
        selection.removeAllRanges()
        selection.addRange(newRange)
        savedSelectionRef.current = newRange.cloneRange()
      } else if (styles.color) {
        const newRange = selection.getRangeAt(0)
        savedSelectionRef.current = newRange.cloneRange()
      }

      emitChange()
      refreshToolbarState()
    },
    [emitChange, focusEditor, refreshToolbarState, restoreSelection]
  )

  const runCommand = useCallback(
    (command: string, commandValue?: string) => {
      focusEditor()
      document.execCommand('styleWithCSS', false, 'true')
      document.execCommand(command, false, commandValue)

      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        savedSelectionRef.current = selection.getRangeAt(0).cloneRange()
      }

      emitChange()
      refreshToolbarState()
      editorRef.current?.focus({ preventScroll: true })
      restoreSelection()
    },
    [emitChange, focusEditor, refreshToolbarState, restoreSelection]
  )

  const applyFontSize = useCallback(
    (raw: string) => {
      const size = parseFontSize(raw)
      if (!size) return
      applyInlineStyle({ fontSize: `${size}px` })
      setToolbar((prev) => ({ ...prev, fontSize: formatFontSize(size) }))
      setFontSizeInput(formatFontSize(size))
      editorRef.current?.focus({ preventScroll: true })
      restoreSelection()
    },
    [applyInlineStyle, restoreSelection]
  )

  const applyColor = useCallback(
    (color: string) => {
      applyInlineStyle({ color })
      setToolbar((prev) => ({ ...prev, color }))
      setColorAnchor(null)
      editorRef.current?.focus({ preventScroll: true })
      restoreSelection()
    },
    [applyInlineStyle, restoreSelection]
  )

  const preserveEditorSelection = useCallback(
    (e: { preventDefault: () => void; target: EventTarget }) => {
      saveSelection()
      const target = e.target as HTMLElement
      if (
        target.closest('input') ||
        target.closest('.MuiAutocomplete-root') ||
        target.closest('.MuiAutocomplete-popper')
      ) {
        return
      }
      e.preventDefault()
    },
    [saveSelection]
  )

  const formatButtonHandlers = (command: string) => ({
    onPointerDown: (e: React.PointerEvent) => {
      if (!isTouchPointer(e)) return
      e.preventDefault()
      runCommand(command)
    },
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault()
      runCommand(command)
    },
  })

  const activeButtonSx = (active: boolean) => ({
    borderRadius: 1,
    p: 0.5,
    width: isMobile ? 36 : 28,
    height: isMobile ? 36 : 28,
    touchAction: 'manipulation',
    color: active ? 'text.primary' : 'text.secondary',
    bgcolor: active ? 'action.selected' : 'transparent',
    '&:hover': { bgcolor: active ? 'action.selected' : 'grey.100' },
  })

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    const next = toEditorHtml(value || '')
    const valueIsEmpty = !value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    if (document.activeElement === el && !valueIsEmpty) return
    if (el.innerHTML !== next) {
      el.innerHTML = next
    }
    if (valueIsEmpty && document.activeElement === el) {
      el.blur()
    }
  }, [value, editorKey])

  useEffect(() => {
    const onSelectionChange = () => {
      const selection = window.getSelection()
      if (!selection?.anchorNode || !editorRef.current?.contains(selection.anchorNode)) {
        return
      }
      saveSelection()
      refreshToolbarState()
    }

    document.addEventListener('selectionchange', onSelectionChange)
    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [refreshToolbarState, saveSelection])

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      {showToolbar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.25,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            mb: 1,
            touchAction: 'manipulation',
          }}
          onMouseDown={preserveEditorSelection}
          onPointerDown={(e) => {
            if (!isTouchPointer(e)) return
            preserveEditorSelection(e)
          }}
        >
          <Tooltip title="Fetstil">
            <IconButton
              size="small"
              sx={activeButtonSx(toolbar.bold)}
              {...formatButtonHandlers('bold')}
            >
              <FormatBoldIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kursiv">
            <IconButton
              size="small"
              sx={activeButtonSx(toolbar.italic)}
              {...formatButtonHandlers('italic')}
            >
              <FormatItalicIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Understruken">
            <IconButton
              size="small"
              sx={activeButtonSx(toolbar.underline)}
              {...formatButtonHandlers('underline')}
            >
              <FormatUnderlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {isMobile ? (
            <>
              <Box
                component="button"
                type="button"
                aria-label="Teckenstorlek"
                onPointerDown={(e) => {
                  e.preventDefault()
                  saveSelection()
                  setFontSizeMenuAnchor(e.currentTarget)
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  saveSelection()
                  setFontSizeMenuAnchor(e.currentTarget)
                }}
                sx={{
                  ...fontSizeFieldSx,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  minHeight: 36,
                  px: 0.75,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                  bgcolor: 'common.white',
                  color: 'text.primary',
                  fontSize: '0.8125rem',
                  touchAction: 'manipulation',
                  cursor: 'pointer',
                }}
              >
                {parseFontSize(fontSizeInput) ?? DEFAULT_FONT_SIZE_PX}
              </Box>
              <Menu
                anchorEl={fontSizeMenuAnchor}
                open={Boolean(fontSizeMenuAnchor)}
                onClose={() => {
                  setFontSizeMenuAnchor(null)
                  keepSelection()
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableAutoFocus
                disableAutoFocusItem
                disableRestoreFocus
                disableEnforceFocus
                MenuListProps={{
                  onPointerDown: (e) => e.preventDefault(),
                  onMouseDown: (e) => e.preventDefault(),
                }}
              >
                {FONT_SIZE_PRESETS.map((size) => (
                  <MenuItem
                    key={size}
                    selected={String(parseFontSize(fontSizeInput) ?? '') === size}
                    onPointerDown={(e) => {
                      e.preventDefault()
                      if (!isTouchPointer(e)) return
                      applyFontSize(size)
                      setFontSizeMenuAnchor(null)
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      applyFontSize(size)
                      setFontSizeMenuAnchor(null)
                    }}
                    sx={{ minHeight: 44, fontSize: '0.9375rem' }}
                  >
                    {size}px
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
          <Autocomplete
            freeSolo
            size="small"
            open={fontSizeOpen}
            openOnFocus
            options={FONT_SIZE_PRESETS}
            filterOptions={(options) => options}
            value={
              (() => {
                const n = parseFontSize(fontSizeInput)
                const key = n ? String(n) : ''
                return FONT_SIZE_PRESETS.includes(key) ? key : null
              })()
            }
            inputValue={fontSizeInput}
            onOpen={openFontSizeDropdown}
            onChange={(_, val) => {
              if (!val) return
              fontSizeEditingRef.current = false
              fontSizeIgnoreCloseRef.current = true
              setFontSizeOpen(false)
              applyFontSize(val)
              requestAnimationFrame(() => {
                fontSizeIgnoreCloseRef.current = false
              })
            }}
            onInputChange={(_, val, reason) => {
              if (reason !== 'input' || !fontSizeEditingRef.current) return
              const digits = val.replace(/\D/g, '')
              setFontSizeInput(digits ? `${digits}px` : '')
            }}
            onClose={closeFontSizeDropdown}
            disableClearable
            componentsProps={{
              popper: {
                style: { zIndex: 1500 },
                onMouseDown: (e: { preventDefault: () => void }) => e.preventDefault(),
              },
              popupIndicator: {
                onMouseDown: (e: { stopPropagation: () => void; preventDefault: () => void }) => {
                  e.stopPropagation()
                  e.preventDefault()
                  openFontSizeDropdown()
                },
              },
            }}
            sx={fontSizeFieldSx}
            onMouseDown={(e) => {
              e.stopPropagation()
              openFontSizeDropdown()
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                onMouseDown={(e) => {
                  e.stopPropagation()
                  openFontSizeDropdown()
                }}
                onClick={() => openFontSizeDropdown()}
                onFocus={openFontSizeDropdown}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    fontSizeEditingRef.current = false
                    setFontSizeOpen(false)
                    applyFontSize(fontSizeInput)
                  }
                  if (e.key === 'Escape') {
                    fontSizeEditingRef.current = false
                    setFontSizeOpen(false)
                    setFontSizeInput(toolbar.fontSize)
                  }
                }}
                onBlur={(e) => {
                  const next = e.relatedTarget as HTMLElement | null
                  if (
                    next?.closest('.MuiAutocomplete-root') ||
                    next?.closest('.MuiAutocomplete-popper')
                  ) {
                    return
                  }
                  fontSizeEditingRef.current = false
                  setFontSizeOpen(false)
                  setFontSizeInput(toolbar.fontSize)
                }}
                inputProps={{
                  ...params.inputProps,
                  inputMode: 'numeric',
                }}
              />
            )}
          />
          )}

          <Tooltip title="Textfärg">
            <IconButton
              size="small"
              sx={{
                borderRadius: 1,
                p: 0.5,
                width: isMobile ? 36 : 28,
                height: isMobile ? 36 : 28,
                touchAction: 'manipulation',
                '&:hover': { bgcolor: 'grey.100' },
              }}
              onPointerDown={(e) => {
                if (!isTouchPointer(e)) return
                e.preventDefault()
                saveSelection()
                setColorAnchor(e.currentTarget)
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                saveSelection()
                setColorAnchor(e.currentTarget)
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  lineHeight: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary' }}
                >
                  A
                </Box>
                <Box
                  sx={{
                    width: 14,
                    height: 2,
                    borderRadius: 0.5,
                    bgcolor: toolbar.color,
                    mt: 0.25,
                  }}
                />
              </Box>
            </IconButton>
          </Tooltip>

          <Tooltip title="Punktlista">
            <IconButton
              size="small"
              sx={activeButtonSx(toolbar.unorderedList)}
              {...formatButtonHandlers('insertUnorderedList')}
            >
              <FormatListBulletedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numrerad lista">
            <IconButton
              size="small"
              sx={activeButtonSx(toolbar.orderedList)}
              {...formatButtonHandlers('insertOrderedList')}
            >
              <FormatListNumberedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => {
          setColorAnchor(null)
          keepSelection()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ zIndex: 1500 }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, maxWidth: 160 }}>
          {TEXT_COLORS.map((color) => (
            <Box
              key={color}
              onPointerDown={(e) => {
                if (!isTouchPointer(e)) return
                e.preventDefault()
                applyColor(color)
              }}
              onMouseDown={(e) => {
                e.preventDefault()
                applyColor(color)
              }}
              sx={{
                width: isMobile ? 36 : 24,
                height: isMobile ? 36 : 24,
                borderRadius: '50%',
                bgcolor: color,
                cursor: 'pointer',
                touchAction: 'manipulation',
                border: '2px solid',
                borderColor: toolbar.color === color ? 'primary.main' : 'grey.300',
              }}
            />
          ))}
        </Box>
      </Popover>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => {
          emitChange()
          refreshToolbarState()
        }}
        onBlur={saveSelection}
        onKeyUp={() => {
          saveSelection()
          refreshToolbarState()
        }}
        onMouseUp={() => {
          saveSelection()
          refreshToolbarState()
        }}
        onTouchEnd={() => {
          saveSelection()
          refreshToolbarState()
        }}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          outline: 'none',
          fontSize: DEFAULT_FONT_SIZE,
          lineHeight: 1.6,
          color: 'text.primary',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            pointerEvents: 'none',
          },
          '& ul, & ol': { pl: 3, my: 0.5 },
          '& *': {
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          },
        }}
      />
    </Box>
  )
}

export default RichTextEditor
